import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Circle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";

type ChangeEvent = {
  id: number;
  repo: string;
  kind: string;
  resourceId: number;
  payload: Record<string, unknown> | null;
  emittedAt: string;
  consumedAt: string | null;
  matches: string[];
};

type EventPage = {
  events: ChangeEvent[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

const KIND_STYLES: Record<string, { label: string; color: string }> = {
  "pr.opened":       { label: "PR opened",       color: "bg-green-500/15 text-green-700 dark:text-green-400" },
  "pr.closed":       { label: "PR closed",        color: "bg-red-500/15 text-red-700 dark:text-red-400" },
  "pr.merged":       { label: "PR merged",        color: "bg-purple-500/15 text-purple-700 dark:text-purple-400" },
  "pr.updated":      { label: "PR updated",       color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  "pr.review_added": { label: "Review added",     color: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400" },
  "issue.opened":    { label: "Issue opened",     color: "bg-green-500/15 text-green-700 dark:text-green-400" },
  "issue.closed":    { label: "Issue closed",     color: "bg-red-500/15 text-red-700 dark:text-red-400" },
  "issue.commented": { label: "Issue comment",    color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  "check.started":   { label: "Check started",   color: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
  "check.completed": { label: "Check completed",  color: "bg-teal-500/15 text-teal-700 dark:text-teal-400" },
};

function kindStyle(kind: string) {
  return KIND_STYLES[kind] ?? { label: kind, color: "bg-muted text-muted-foreground" };
}

function fmt(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function PayloadCell({ payload }: { payload: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);
  if (!payload) return <span className="text-muted-foreground/40 text-xs">—</span>;
  const preview = Object.keys(payload).slice(0, 3).join(", ");
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
      >
        {open ? "hide" : preview || "expand"}
      </button>
      {open && (
        <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  );
}

function useEvents(page: number, repo: string, kind: string) {
  const [data, setData] = useState<EventPage | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (repo) params.set("repo", repo);
      if (kind) params.set("kind", kind);
      const res = await fetch(`/api/repos/events?${params}`);
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [page, repo, kind]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, refresh: load };
}

export default function Events() {
  const [page, setPage] = useState(1);
  const [repoFilter, setRepoFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [pendingRepo, setPendingRepo] = useState("");
  const [pendingKind, setPendingKind] = useState("");

  const { data, loading } = useEvents(page, repoFilter, kindFilter);

  const applyFilters = () => {
    setRepoFilter(pendingRepo);
    setKindFilter(pendingKind);
    setPage(1);
  };

  const clearFilters = () => {
    setPendingRepo("");
    setPendingKind("");
    setRepoFilter("");
    setKindFilter("");
    setPage(1);
  };

  const allKinds = Object.keys(KIND_STYLES);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Event stream"
        description="Change events emitted by the GitHub replica sync — the trigger source for workflows."
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Repository</label>
          <input
            className="h-8 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring w-48"
            placeholder="owner/repo"
            value={pendingRepo}
            onChange={e => setPendingRepo(e.target.value)}
            onKeyDown={e => e.key === "Enter" && applyFilters()}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Event kind</label>
          <select
            className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            value={pendingKind}
            onChange={e => setPendingKind(e.target.value)}
          >
            <option value="">All kinds</option>
            {allKinds.map(k => (
              <option key={k} value={k}>{KIND_STYLES[k].label}</option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={applyFilters}>Filter</Button>
        {(repoFilter || kindFilter) && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.events.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-16 text-center">
          <Circle className="h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No events yet. Sync a project to start seeing the stream.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                  <th className="py-2 pl-4 pr-3 text-left font-medium w-8">#</th>
                  <th className="py-2 px-3 text-left font-medium">Time</th>
                  <th className="py-2 px-3 text-left font-medium">Kind</th>
                  <th className="py-2 px-3 text-left font-medium">Triggers</th>
                  <th className="py-2 px-3 text-left font-medium">Repository</th>
                  <th className="py-2 px-3 text-left font-medium">Resource</th>
                  <th className="py-2 px-3 text-left font-medium">Status</th>
                  <th className="py-2 pl-3 pr-4 text-left font-medium">Payload</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.events.map(ev => {
                  const { label, color } = kindStyle(ev.kind);
                  return (
                    <tr key={ev.id} className="hover:bg-muted/20 transition-colors align-top">
                      <td className="py-3 pl-4 pr-3 text-muted-foreground/50 tabular-nums">{ev.id}</td>
                      <td className="py-3 px-3 whitespace-nowrap tabular-nums text-muted-foreground">
                        {fmt(ev.emittedAt)}
                      </td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
                          {label}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        {ev.matches && ev.matches.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {ev.matches.map(m => (
                              <span
                                key={m}
                                title={`This event would dispatch the ${m} workflow`}
                                className="inline-flex items-center gap-1 rounded-full bg-indigo-500/15 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                              >
                                <Zap className="h-3 w-3" />
                                {m}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3 font-mono text-xs text-muted-foreground">{ev.repo}</td>
                      <td className="py-3 px-3 tabular-nums text-muted-foreground">
                        {ev.resourceId > 0 ? `#${ev.resourceId}` : "—"}
                      </td>
                      <td className="py-3 px-3">
                        {ev.consumedAt ? (
                          <Badge variant="secondary" className="text-xs">consumed</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-600 dark:text-yellow-400">pending</Badge>
                        )}
                      </td>
                      <td className="py-3 pl-3 pr-4">
                        <PayloadCell payload={ev.payload} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.total.toLocaleString()} event{data.total !== 1 ? "s" : ""}
              {" · "}page {data.page} of {data.totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="icon" variant="outline" className="h-7 w-7"
                disabled={data.page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="icon" variant="outline" className="h-7 w-7"
                disabled={data.page >= data.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
